import React, { useState, useEffect, useRef, useId, useMemo } from 'react';
import { Form, InputGroup, Button, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { FiSearch, FiFilter, FiX } from 'react-icons/fi';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FilterField {
  /** Key sent to the backend (e.g. "status", "role"). */
  name: string;
  /** Human-readable label shown above the filter input. */
  label: string;
  type: 'text' | 'select' | 'number' | 'date';
  placeholder?: string;
  /**
   * Required when type === 'select'.
   * Options that share the same `group` render inside an `<optgroup>`.
   * Options without `group` render as top-level `<option>`s.
   */
  options?: { value: string | number; label: string; group?: string }[];
  /**
   * When false, omit the leading empty "All" option for select fields.
   * Defaults to true. Use when options already include an all/sentinel value.
   */
  includeEmptyOption?: boolean;
  /**
   * Bootstrap column width (1–12). Defaults to 4 (3 per row on md+).
   * Use 6 for 2-per-row or 12 for full-width.
   */
  colWidth?: number;
}

export interface SearchBarProps {
  /** Placeholder text for the main search input. */
  placeholder?: string;
  /**
   * Pre-fill the text input on first render (e.g. when initialising from a URL
   * param). Pass `search.initialQuery` from `useSearch` with `syncUrl: true`.
   */
  initialQuery?: string;
  /**
   * Pre-fill filter inputs on first render or when externally-restored filters
   * change (for example, URL-driven filters on navigation).
   */
  initialFilters?: Record<string, any>;
  /**
   * Filter field definitions. When omitted (or empty), the filter button is
   * hidden and only the freetext input is rendered.
   */
  filterFields?: FilterField[];
  /**
   * Called on every keystroke (un-debounced). Pass `search.setQuery` from
   * `useSearch` — the hook handles debouncing internally.
   */
  onQueryChange: (query: string) => void;
  /**
   * Called when the user clicks "Apply Filters" in the filter panel.
   * Pass `search.setFilters` from `useSearch`.
   */
  onFiltersApply: (filters: Record<string, any>) => void;
  /**
   * Called when the X clear button is clicked.
   * Pass `search.reset` from `useSearch` so both query and filters are reset.
   */
  onClear: () => void;
  /** Disables the input while a fetch is in progress. */
  loading?: boolean;
  /**
   * Number of currently-active filters to show in the badge on the filter button.
   * Pass `search.activeFilterCount` from `useSearch`.
   */
  activeFilterCount?: number;
  /**
   * When true, the filter panel shows an info notice that filters are paused
   * because freetext is active. Pass `search.filtersIgnored` from `useSearch`.
   */
  filtersIgnored?: boolean;
  /**
   * Optional control rendered inline before the search input (e.g. a scope selector),
   * so it reads as one toolbar. Stacks full-width above the search row on mobile.
   */
  leadingControl?: React.ReactNode;
  testId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Generic search bar with an optional collapsible filter panel.
 *
 * Pairs with the `useSearch` hook:
 *
 *   <SearchBar
 *     placeholder="Search users…"
 *     filterFields={FILTER_FIELDS}
 *     initialQuery={search.initialQuery}
 *     onQueryChange={search.setQuery}
 *     onFiltersApply={search.setFilters}
 *     onClear={search.reset}
 *     loading={search.loading}
 *     activeFilterCount={search.activeFilterCount}
 *     filtersIgnored={search.filtersIgnored}
 *   />
 *
 * Freetext and filters are sent together — they are not mutually exclusive.
 * Both values are forwarded to the API on every search.
 */
export function SearchBar({
  placeholder = 'Search…',
  initialQuery = '',
  initialFilters = {},
  filterFields,
  onQueryChange,
  onFiltersApply,
  onClear,
  loading = false,
  activeFilterCount = 0,
  filtersIgnored = false,
  leadingControl,
  testId = 'search-bar',
}: SearchBarProps) {
  const inputId = useId();
  const containerRef = useRef<HTMLDivElement>(null);

  // ─── Local state ────────────────────────────────────────────────────────
  // Initialise from `initialQuery` so URL-restored searches populate the input.
  const [inputValue, setInputValue] = useState(initialQuery);
  const [filterOpen, setFilterOpen] = useState(false);
  /** Pending values inside the filter panel — not applied until Apply is clicked. */
  const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({});

  const hasFilterFields = Boolean(filterFields && filterFields.length > 0);
  const initialFiltersKey = JSON.stringify(initialFilters);
  const stableInitialFilters = useMemo(() => initialFilters, [initialFiltersKey]);

  useEffect(() => {
    setInputValue(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    setPendingFilters(stableInitialFilters);
  }, [stableInitialFilters]);

  useEffect(() => {
    if (activeFilterCount === 0) {
      setPendingFilters(stableInitialFilters);
    }
  }, [activeFilterCount, stableInitialFilters]);

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInputValue(v);
    onQueryChange(v);
    // useSearch.setQuery will clear applied filters automatically (mutual
    // exclusion) — no extra call needed here.
  };

  const handleClear = () => {
    setInputValue('');
    setPendingFilters({});
    setFilterOpen(false);
    onClear();
  };

  const handleFilterFieldChange = (name: string, value: any) => {
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    // Strip empty/null values before sending upward.
    const cleaned = Object.fromEntries(
      Object.entries(pendingFilters).filter(
        ([, v]) => v !== undefined && v !== null && v !== '',
      ),
    );
    onFiltersApply(cleaned);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setPendingFilters({});
    onFiltersApply({});
  };

  const toggleFilterPanel = () => setFilterOpen((prev) => !prev);

  // ─── Close filter panel on outside click ───────────────────────────────
  useEffect(() => {
    if (!filterOpen) return;

    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setFilterOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [filterOpen]);

  // ─── Render helpers ────────────────────────────────────────────────────

  const renderSelectOptions = (
    options: FilterField['options']
  ): React.ReactNode => {
    if (!options?.length) return null;

    const ungrouped: { value: string | number; label: string }[] = [];
    const groups = new Map<string, { value: string | number; label: string }[]>();

    for (const opt of options) {
      if (opt.value === '') continue;
      if (opt.group) {
        const list = groups.get(opt.group) ?? [];
        list.push({ value: opt.value, label: opt.label });
        groups.set(opt.group, list);
      } else {
        ungrouped.push({ value: opt.value, label: opt.label });
      }
    }

    return (
      <>
        {ungrouped.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
        {Array.from(groups.entries()).map(([groupLabel, groupOpts]) => (
          <optgroup key={groupLabel} label={groupLabel}>
            {groupOpts.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </optgroup>
        ))}
      </>
    );
  };

  const renderFilterField = (field: FilterField) => {
    const fieldId = `${testId}-filter-${field.name}`;
    const value = pendingFilters[field.name] ?? '';

    switch (field.type) {
      case 'select':
        return (
          <Form.Group key={field.name} controlId={fieldId}>
            <Form.Label className="ntcog-search-filter-label">{field.label}</Form.Label>
            <Form.Select
              size="sm"
              value={value}
              onChange={(e) => handleFilterFieldChange(field.name, e.target.value)}
              data-testid={fieldId}
            >
              {field.includeEmptyOption !== false && (
                <option value="">{field.placeholder ?? 'All'}</option>
              )}
              {renderSelectOptions(field.options)}
            </Form.Select>
          </Form.Group>
        );

      case 'date':
        return (
          <Form.Group key={field.name} controlId={fieldId}>
            <Form.Label className="ntcog-search-filter-label">{field.label}</Form.Label>
            <Form.Control
              type="date"
              size="sm"
              value={value}
              onChange={(e) => handleFilterFieldChange(field.name, e.target.value)}
              data-testid={fieldId}
            />
          </Form.Group>
        );

      case 'number':
        return (
          <Form.Group key={field.name} controlId={fieldId}>
            <Form.Label className="ntcog-search-filter-label">{field.label}</Form.Label>
            <Form.Control
              type="number"
              size="sm"
              placeholder={field.placeholder}
              value={value}
              onChange={(e) => handleFilterFieldChange(field.name, e.target.value)}
              data-testid={fieldId}
            />
          </Form.Group>
        );

      default:
        return (
          <Form.Group key={field.name} controlId={fieldId}>
            <Form.Label className="ntcog-search-filter-label">{field.label}</Form.Label>
            <Form.Control
              type="text"
              size="sm"
              placeholder={field.placeholder ?? `Filter by ${field.label.toLowerCase()}…`}
              value={value}
              onChange={(e) => handleFilterFieldChange(field.name, e.target.value)}
              data-testid={fieldId}
            />
          </Form.Group>
        );
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div
      ref={containerRef}
      className={`ntcog-search-bar${filterOpen ? ' ntcog-search-bar--filter-open' : ''}`}
      data-testid={testId}
    >
      {/* ── Main search row (optional leading control, e.g. a scope selector) ── */}
      <div className="d-flex flex-column flex-md-row gap-0 align-items-stretch">
        {leadingControl && (
          <div className="ntcog-search-leading flex-shrink-0">{leadingControl}</div>
        )}
        <InputGroup className="flex-grow-1">
        {/* Left addon (search icon) — rendered as InputGroup.Text so Bootstrap
            joins it seamlessly with no border between icon and input. */}
        <InputGroup.Text className="ntcog-search-icon-addon" aria-hidden="true">
          {loading
            ? <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem', borderWidth: '0.15em' }} />
            : <FiSearch />}
        </InputGroup.Text>

        <Form.Control
          id={inputId}
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          aria-label={placeholder}
          aria-busy={loading}
          className="ntcog-search-input"
          data-testid={`${testId}-input`}
          autoComplete="off"
          spellCheck={false}
        />

        {/* Clear button — visible when there is text in the input */}
        {inputValue && (
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={handleClear}
            aria-label="Clear search"
            className="ntcog-search-clear-btn"
            data-testid={`${testId}-clear`}
          >
            <FiX aria-hidden="true" />
          </Button>
        )}

        {/* Right addon (filter icon) — also rendered as InputGroup.Text, exactly
            mirroring the left search icon. Bootstrap joins it flush with the
            input: no shared-border overlap, no extra margin tricks. */}
        {hasFilterFields && (
          <InputGroup.Text
            role="button"
            tabIndex={0}
            onClick={toggleFilterPanel}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFilterPanel(); } }}
            aria-expanded={filterOpen}
            aria-controls={`${testId}-filter-panel`}
            aria-label={
              activeFilterCount > 0
                ? `Filters (${activeFilterCount} active) — click to adjust`
                : 'Show filter options'
            }
            title={
              activeFilterCount > 0
                ? `${activeFilterCount} filter${activeFilterCount !== 1 ? 's' : ''} active — click to adjust`
                : 'Click to show filter options'
            }
            className={`ntcog-search-filter-btn position-relative${activeFilterCount > 0 ? ' ntcog-search-filter-btn--active' : ''}`}
            data-testid={`${testId}-filter-btn`}
          >
            <FiFilter aria-hidden="true" />
            {activeFilterCount > 0 && (
              <Badge
                bg="warning"
                text="dark"
                pill
                className="ntcog-search-filter-badge position-absolute top-0 start-100 translate-middle"
                aria-label={`${activeFilterCount} active filter${activeFilterCount !== 1 ? 's' : ''}`}
              >
                {activeFilterCount}
              </Badge>
            )}
          </InputGroup.Text>
        )}
        </InputGroup>
      </div>

      {/* ── Filter panel ── */}
      {hasFilterFields && filterOpen && (
        <div
          id={`${testId}-filter-panel`}
          className="ntcog-search-filter-panel p-2"
          role="region"
          aria-label="Search filters"
          data-testid={`${testId}-filter-panel`}
        >
          <Row className="g-3 mb-3">
            {filterFields!.map((field) => (
              <Col
                key={field.name}
                xs={12}
                sm={6}
                md={field.colWidth ?? 4}
              >
                {renderFilterField(field)}
              </Col>
            ))}
          </Row>

          <div className="d-flex gap-2 justify-content-end align-items-center border-top pt-3">
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={handleClearFilters}
              className="ntcog-search-clear-filters-btn text-muted text-decoration-none"
              data-testid={`${testId}-clear-filters`}
            >
              Clear filters
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleApplyFilters}
              data-testid={`${testId}-apply-filters`}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
